import { sanitizeInstrumentationPayload } from './instrumentation';

// Simple logging utility
// In production, this could be replaced with a service like Sentry or LogRocket

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

const CURRENT_LEVEL = process.env.NODE_ENV === 'test'
  ? Number.POSITIVE_INFINITY
  : process.env.NODE_ENV === 'production'
    ? LOG_LEVELS.WARN
    : LOG_LEVELS.DEBUG;

const safeErrorMetadata = (error) => {
  if (!error || typeof error !== 'object') return error;

  const metadata = {
    name: error.name || 'Error',
  };

  ['code', 'status', 'reason', 'provider'].forEach((key) => {
    if (error[key] != null) {
      metadata[key] = error[key];
    }
  });

  return sanitizeInstrumentationPayload(metadata);
};

const sanitizeLogValue = (value) => {
  if (value instanceof Error) {
    return safeErrorMetadata(value);
  }

  if (value && typeof value === 'object') {
    return sanitizeInstrumentationPayload(value);
  }

  return sanitizeInstrumentationPayload({ value }).value;
};

const sanitizeLogArgs = (args = []) => args.map(sanitizeLogValue);
const sanitizeLogDetails = (details = {}) => sanitizeInstrumentationPayload(details || {});

const safeErrorEventDetails = (error, details = {}) => {
  const rawErrorMetadata = error && typeof error === 'object'
    ? {
        error_type: error.name || 'Error',
        error_code: error.code || error.errorCode || null,
        status: error.status || error.statusCode || null,
        provider: error.provider || null,
      }
    : {
        error_type: error == null ? 'UnknownError' : typeof error,
      };
  const errorMetadata = Object.fromEntries(
    Object.entries(rawErrorMetadata).filter(([, value]) => value != null)
  );

  return sanitizeLogDetails({
    ...details,
    ...errorMetadata,
  });
};

const logger = {
  debug: (...args) => {
    if (CURRENT_LEVEL <= LOG_LEVELS.DEBUG) {
      console.log('[DEBUG]', ...sanitizeLogArgs(args));
    }
  },

  info: (...args) => {
    if (CURRENT_LEVEL <= LOG_LEVELS.INFO) {
      console.info('[INFO]', ...sanitizeLogArgs(args));
    }
  },

  warn: (...args) => {
    if (CURRENT_LEVEL <= LOG_LEVELS.WARN) {
      console.warn('[WARN]', ...sanitizeLogArgs(args));
    }
  },

  error: (...args) => {
    if (CURRENT_LEVEL <= LOG_LEVELS.ERROR) {
      console.error('[ERROR]', ...sanitizeLogArgs(args));
    }
  },

  errorEvent: (event, details = {}) => {
    if (CURRENT_LEVEL <= LOG_LEVELS.ERROR) {
      console.error('[ERROR_EVENT]', JSON.stringify({ event, ...sanitizeLogDetails(details) }));
    }
  },

  infoEvent: (event, details = {}) => {
    if (CURRENT_LEVEL <= LOG_LEVELS.INFO) {
      console.info('[INFO_EVENT]', JSON.stringify({ event, ...sanitizeLogDetails(details) }));
    }
  }
};

export default logger;
export { safeErrorEventDetails, sanitizeLogArgs, sanitizeLogDetails };
